"use client";

import { useEffect } from "react";

interface Props {
  /** Event name, must match SplashWrapper's `readyEvent`. Default: "app-data-ready" */
  event?: string;
}

/**
 * Meldet dem `SplashWrapper`, dass die Huelle steht — gefeuert nach dem ersten
 * Bild, das der Browser mit hydriertem Inhalt zeichnet.
 *
 * ZWEI VERWORFENE WEGE, damit sie nicht zurueckkommen:
 *
 * 1. `requestIdleCallback` OHNE Deadline (bis 0.113.1): beim Cold-Start ist der
 *    Hauptthread nie „idle" — Hydration, Provider und `getUser()` schieben den
 *    Rueckruf beliebig weit; das Ereignis blieb aus und der Splash haing bis zum
 *    Notbremsen-Timeout.
 * 2. `requestIdleCallback` MIT Deadline 1500 ms (bis 0.209.0): die Deadline
 *    wurde damit zum Normalfall statt zur Ausnahme. Der Splash richtete sich
 *    nach einer Uhr, nicht nach dem Ladezustand.
 *
 * Zwei verschachtelte `requestAnimationFrame` sind das, was hier gemeint war:
 * der zweite laeuft, nachdem der Browser das erste Bild nach der Hydration
 * tatsaechlich gezeichnet hat. Frueher zu melden hiesse, gegen einen noch
 * leeren Bildschirm abzublenden.
 *
 * DER TIMER DANEBEN IST KEIN GUERTEL-UND-HOSENTRAEGER: in einem Tab im
 * Hintergrund — „im neuen Tab oeffnen", Strg+Klick aus dem App-Wechsel —
 * zeichnet der Browser nicht, also laeuft `requestAnimationFrame` gar nicht.
 * Ohne den Timer stuende der Splash dort bis zur Notbremse und waere genau dann
 * noch zu sehen, wenn der Nutzer hinwechselt. Timer werden im Hintergrund
 * gedrosselt, aber sie feuern. Im sichtbaren Tab kommt rAF nach einem Frame und
 * damit lange zuerst.
 *
 * ── ABER ER GRIFF TROTZDEM, UND ZWAR JEDES MAL ──────────────────────────────
 *
 * „Der Timer greift dann nie" stand hier bis 0.372.0 und war falsch: der
 * Rueckfall wurde nur beim UNMOUNT abgeraeumt, nicht wenn der rAF-Pfad schon
 * gemeldet hatte. `app-data-ready` feuerte deshalb aus EINEM Mount zweimal —
 * einmal nach zwei Bildern, einmal 300 ms spaeter. Gemessen am 2026-09-03 in
 * `janeway-mail-client` bei 1x, 4x und 6x gedrosselter CPU, in allen drei
 * Laeufen mit exakt diesem Abstand.
 *
 * Folgenlos war es nur, weil `SplashWrapper` die Meldung in einem Boolean
 * einsammelt. Ein zweiter Empfaenger, der zaehlt oder misst, haette die Zahl
 * still verdoppelt — die Art Fehler, die man erst in einer Kennzahl wiederfindet.
 */
const HINTERGRUND_RUECKFALL_MS = 300;

export function SplashReadySignal({ event = "app-data-ready" }: Props) {
  useEffect(() => {
    let gemeldet = false;
    const melden = () => {
      if (gemeldet) return;
      gemeldet = true;
      clearTimeout(rueckfall);
      window.dispatchEvent(new Event(event));
    };
    let zweiter = 0;
    const erster = requestAnimationFrame(() => {
      zweiter = requestAnimationFrame(melden);
    });
    const rueckfall = setTimeout(melden, HINTERGRUND_RUECKFALL_MS);
    return () => {
      cancelAnimationFrame(erster);
      if (zweiter) cancelAnimationFrame(zweiter);
      clearTimeout(rueckfall);
    };
  }, [event]);
  return null;
}
